// /inventory/report — Inventory report (ops simulation T13), modelled on Odoo's
// forecasted inventory + reordering rules. Lives in the Inventory section beside
// Valuation, not in the plan-gated reports: stock levels are core.
//
// One row per product / variant per location: on hand split into batches
// (with expiry) or serials, incoming (open POs + transfers in transit),
// outgoing in the period (sold + transferred out; already in on hand), forecast
// (on hand + incoming − planned outgoing), days of stock left from the last 30 days'
// sales, and min/max — which can be edited on the row.
// Excel export is a CSV (opens in Excel) of every row; PDF is the browser's print dialog.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { TimeRangeValue } from '@bbpos/ui';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  downloadCsv,
  KpiIcons,
} from '@/features/reports';
import type { ReportExportFormat } from '@/features/reports';
import { useAuthStore } from '@/stores/use-auth-store';
import { inventoryReportService } from '@/services/inventory-report.service';
import type { InventoryReportRow } from '@/services/inventory-report.service';

const PAGE_SIZE = 50;
// Same 30-day window the days-of-stock sales rate uses.
const DEFAULT_RANGE: TimeRangeValue = { preset: 'last30' };

const qty = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 3 });

export function variantLabel(row: Pick<InventoryReportRow, 'variant_attributes'>): string {
  const attrs = row.variant_attributes;
  if (!attrs) return '';
  return Object.entries(attrs)
    .map(([k, v]) => `${k}: ${v}`)
    .join(' · ');
}

/** On hand as one line per batch / serial, for the CSV and the print view. */
export function onHandBreakdown(row: InventoryReportRow): string {
  const parts = row.batches.map((b) => `${b.batch_number} (exp ${b.expiry_date}) ${qty(b.quantity)}`);
  if (row.serials.length) parts.push(`Serials: ${row.serials.join(' ')}`);
  if (parts.length && row.untracked_quantity > 0) parts.push(`Untracked ${qty(row.untracked_quantity)}`);
  return parts.join('; ');
}

export function InventoryReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_RANGE);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const filter = useReportFilter(timeRange, locationId);
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filters = {
    start_date: filter.range.from,
    end_date: filter.range.to,
    location_id: locationId,
    search: search || undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'inventory', tenantId, filters.start_date, filters.end_date, locationId ?? 'all', search, page],
    queryFn: () => inventoryReportService.report({ ...filters, page, limit: PAGE_SIZE }),
    staleTime: 30_000,
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const pageIncoming = rows.reduce((t, r) => t + r.incoming, 0);

  const handleExport = async (fmt: ReportExportFormat) => {
    if (fmt === 'pdf') {
      window.print();
      return;
    }
    if (fmt !== 'csv') return;
    setExporting(true);
    try {
      const all = await inventoryReportService.reportAll(filters);
      downloadCsv(
        `inventory-${filters.start_date}-to-${filters.end_date}.csv`,
        [
          { header: 'Location', get: (r: InventoryReportRow) => r.location_name },
          { header: 'Product', get: (r: InventoryReportRow) => r.parent_name ?? r.name },
          { header: 'Variant', get: (r: InventoryReportRow) => variantLabel(r) },
          { header: 'SKU', get: (r: InventoryReportRow) => r.sku },
          { header: 'Unit', get: (r: InventoryReportRow) => r.unit },
          { header: 'On hand', get: (r: InventoryReportRow) => r.on_hand },
          { header: 'Batches / serials', get: (r: InventoryReportRow) => onHandBreakdown(r) },
          { header: 'Incoming (PO)', get: (r: InventoryReportRow) => r.incoming_po },
          { header: 'Incoming (transfers)', get: (r: InventoryReportRow) => r.incoming_transfers },
          { header: 'Outgoing (sold)', get: (r: InventoryReportRow) => r.outgoing_sales },
          { header: 'Outgoing (transfers)', get: (r: InventoryReportRow) => r.outgoing_transfers },
          { header: 'Forecast', get: (r: InventoryReportRow) => r.forecast },
          { header: 'Avg daily sales', get: (r: InventoryReportRow) => r.avg_daily_sales },
          { header: 'Days of stock', get: (r: InventoryReportRow) => r.days_of_stock ?? '' },
          { header: 'Min', get: (r: InventoryReportRow) => r.min_stock },
          { header: 'Max', get: (r: InventoryReportRow) => r.max_stock ?? '' },
          { header: 'Status', get: (r: InventoryReportRow) => STATUS_LABEL[r.stock_status] },
          { header: 'Suggested order', get: (r: InventoryReportRow) => r.suggested_order_qty || '' },
        ],
        all,
      );
    } finally {
      setExporting(false);
    }
  };

  const extraFilters = (
    <div className="w-full sm:w-56">
      <input
        type="search"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder="Search product or SKU"
        aria-label="Search product or SKU"
        className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all duration-200"
      />
    </div>
  );

  const meta = data?.meta;
  const summary = data?.summary;

  return (
    <ReportPageShell
      title="Inventory Report"
      subtitle="On hand by batch, expiry or serial, incoming and outgoing stock, forecast and min/max for every product at every location."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Inventory', href: '/inventory' }, { label: 'Inventory Report' }]}
      accent="teal-success"
      headerActions={
        <ReportExportMenu onExport={handleExport} formats={['csv', 'pdf']} disabled={!rows.length || exporting} />
      }
      filterBar={
        <ReportFilterBar
          timeRange={timeRange}
          onTimeRangeChange={(v) => { setTimeRange(v); setPage(1); }}
          locationId={locationId}
          onLocationChange={(id) => { setLocationId(id); setPage(1); }}
          extraFilters={extraFilters}
        />
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'items', title: 'Stock Items', value: String(summary?.total_items ?? 0), subLabel: 'products and variants per location', tone: 'teal', icon: KpiIcons.cart },
              { id: 'below', title: 'Below Min', value: String(summary?.below_min ?? 0), tone: (summary?.below_min ?? 0) > 0 ? 'danger' : 'success', icon: KpiIcons.fire },
              { id: 'above', title: 'Above Max', value: String(summary?.above_max ?? 0), tone: 'warning', icon: KpiIcons.tag },
              { id: 'incoming', title: 'Incoming (this page)', value: qty(pageIncoming), subLabel: 'open POs + in transit', tone: 'indigo', icon: KpiIcons.receipt },
            ]}
          />

          {rows.length === 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
              No products for the selected filters.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 shadow-md animate-fade-in">
              <table className="min-w-full text-sm">
                <thead className="bg-gradient-to-r from-teal-50 to-success-50 dark:from-teal-900/30 dark:to-success-900/20 text-xs uppercase tracking-wide text-neutral-600 dark:text-neutral-300">
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-left">Location</th>
                    <th className="px-3 py-2 text-right">On hand</th>
                    <th className="px-3 py-2 text-right">Incoming</th>
                    <th className="px-3 py-2 text-right">Outgoing</th>
                    <th className="px-3 py-2 text-right">Forecast</th>
                    <th className="px-3 py-2 text-right">Days left</th>
                    <th className="px-3 py-2 text-right">Min / Max</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <InventoryRow key={r.product_id} row={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-end gap-3 mt-6 text-sm print:hidden">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg px-3 py-1.5 bg-white dark:bg-neutral-800 shadow-sm hover:bg-teal-50 disabled:opacity-40 transition-all duration-200"
              >
                Previous
              </button>
              <span className="text-neutral-500 dark:text-neutral-400 tabular-nums">
                Page {meta.page} of {meta.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg px-3 py-1.5 bg-white dark:bg-neutral-800 shadow-sm hover:bg-teal-50 disabled:opacity-40 transition-all duration-200"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </ReportPageShell>
  );
}

const STATUS_LABEL: Record<InventoryReportRow['stock_status'], string> = {
  BELOW_MIN: 'Below min',
  ABOVE_MAX: 'Above max',
  OK: 'OK',
};

const STATUS_CLASS: Record<InventoryReportRow['stock_status'], string> = {
  BELOW_MIN: 'bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300',
  ABOVE_MAX: 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
  OK: 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300',
};

function InventoryRow({ row: r }: { row: InventoryReportRow }) {
  const [editing, setEditing] = useState(false);
  const variant = variantLabel(r);
  return (
    <tr className="align-top even:bg-neutral-50 dark:even:bg-neutral-900/30 hover:bg-teal-50 dark:hover:bg-neutral-700/40 transition-colors animate-slide-up">
      <td className="px-3 py-2">
        <p className="font-medium text-neutral-900 dark:text-white">{r.parent_name ?? r.name}</p>
        {variant && <p className="text-xs text-violet-600 dark:text-violet-300">{variant}</p>}
        <p className="text-xs font-mono text-neutral-500 dark:text-neutral-400">{r.sku}</p>
      </td>
      <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">{r.location_name}</td>
      <td className="px-3 py-2 text-right">
        <p className="font-semibold tabular-nums text-neutral-900 dark:text-white">{qty(r.on_hand)} <span className="text-xs font-normal text-neutral-500">{r.unit}</span></p>
        <OnHandDetail row={r} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <p className="font-semibold text-indigo-700 dark:text-indigo-300">{qty(r.incoming)}</p>
        {r.incoming > 0 && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">PO {qty(r.incoming_po)} · Transit {qty(r.incoming_transfers)}</p>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <p className="font-semibold text-pink-700 dark:text-pink-300">{qty(r.outgoing)}</p>
        {r.outgoing > 0 && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Sold {qty(r.outgoing_sales)} · Out {qty(r.outgoing_transfers)}</p>
        )}
      </td>
      <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.forecast < r.min_stock ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-900 dark:text-white'}`}>
        {qty(r.forecast)}
        {r.planned_outgoing > 0 && (
          <p className="text-xs font-normal text-neutral-500 dark:text-neutral-400">Planned out {qty(r.planned_outgoing)}</p>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {r.days_of_stock == null ? <span className="text-neutral-400">—</span> : <span>{r.days_of_stock}</span>}
        {r.avg_daily_sales > 0 && <p className="text-xs text-neutral-500 dark:text-neutral-400">{qty(r.avg_daily_sales)}/day</p>}
      </td>
      <td className="px-3 py-2 text-right">
        {editing ? (
          <MinMaxEditor row={r} onDone={() => setEditing(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Edit min and max for ${r.sku} at ${r.location_name}`}
            className="rounded-lg px-2 py-1 tabular-nums text-neutral-800 dark:text-neutral-200 hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-all duration-200"
          >
            {qty(r.min_stock)} / {r.max_stock == null ? '—' : qty(r.max_stock)}
          </button>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${STATUS_CLASS[r.stock_status]}`}>
          {STATUS_LABEL[r.stock_status]}
        </span>
        {r.suggested_order_qty > 0 && (
          <p className="mt-1 text-xs text-brand-700 dark:text-brand-300 whitespace-nowrap">Order {qty(r.suggested_order_qty)}</p>
        )}
      </td>
    </tr>
  );
}

function OnHandDetail({ row: r }: { row: InventoryReportRow }) {
  if (!r.batches.length && !r.serials.length) return null;
  return (
    <details className="mt-1 text-left text-xs text-neutral-600 dark:text-neutral-300">
      <summary className="cursor-pointer text-teal-700 dark:text-teal-300 text-right">
        {r.batches.length ? `${r.batches.length} batch${r.batches.length === 1 ? '' : 'es'}` : `${r.serials.length} serial${r.serials.length === 1 ? '' : 's'}`}
      </summary>
      <ul className="mt-1 space-y-0.5">
        {r.batches.map((b) => (
          <li key={`${b.batch_number}-${b.expiry_date}`} className="tabular-nums">
            <span className="font-mono">{b.batch_number}</span> · exp {b.expiry_date} · {qty(b.quantity)}
          </li>
        ))}
        {r.serials.length > 0 && <li className="font-mono break-all">{r.serials.join(', ')}</li>}
        {r.untracked_quantity > 0 && <li>Untracked {qty(r.untracked_quantity)}</li>}
      </ul>
    </details>
  );
}

function MinMaxEditor({ row, onDone }: { row: InventoryReportRow; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [min, setMin] = useState(String(row.min_stock));
  const [max, setMax] = useState(row.max_stock == null ? '' : String(row.max_stock));
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: { min_stock: number; max_stock: number | null }) =>
      inventoryReportService.setStockLevels(row.product_id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reports', 'inventory'] });
      onDone();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Could not save'),
  });

  const submit = () => {
    const minNum = Number(min);
    const maxNum = max.trim() === '' ? null : Number(max);
    if (!Number.isInteger(minNum) || minNum < 0) return setErr('Min must be a whole number, 0 or more');
    if (maxNum != null && (!Number.isFinite(maxNum) || maxNum < minNum)) return setErr('Max must be at least the min');
    setErr(null);
    save.mutate({ min_stock: minNum, max_stock: maxNum });
  };

  const inputClass =
    'w-16 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-1.5 py-1 text-right text-sm tabular-nums text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500';
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1">
        <input aria-label="Min stock" className={inputClass} inputMode="numeric" value={min} onChange={(e) => setMin(e.target.value)} />
        <span className="text-neutral-400">/</span>
        <input aria-label="Max stock" className={inputClass} inputMode="decimal" placeholder="—" value={max} onChange={(e) => setMax(e.target.value)} />
      </div>
      <div className="flex gap-1">
        <button type="button" onClick={onDone} className="rounded-md px-2 py-0.5 text-xs text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200">
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={save.isPending}
          className="rounded-md px-2 py-0.5 text-xs font-semibold text-white bg-gradient-to-r from-teal-600 to-success-600 hover:shadow-md disabled:opacity-50 transition-all duration-200"
        >
          Save
        </button>
      </div>
      {err && <p role="alert" className="text-xs text-danger-600 dark:text-danger-400">{err}</p>}
    </div>
  );
}
